import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useEntity } from '../../context/EntityContext';
import { propertyAPI, unitAPI, bookingFormAPI, developerTemplateAPI } from '../../lib/api';
import { formatCurrency } from '../../lib/utils';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Card, CardContent } from '../../components/ui/card';
import { Separator } from '../../components/ui/separator';
import { Checkbox } from '../../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { toast } from 'sonner';
import {
  Building2, Home, FileText, CreditCard, CheckCircle,
  ArrowLeft, ArrowRight, Loader2, Wand2,
  Download, Eye, ChevronLeft, ChevronRight, Upload, Receipt, ImageIcon
} from 'lucide-react';
import SignaturePad from '../../components/common/SignaturePad';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CreateBooking = () => {
  const { user } = useAuth();
  const { currentEntity } = useEntity();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fileInputRef = useRef(null);

  const preSelectedPropertyId = searchParams.get('property_id');
  const preSelectedUnitId = searchParams.get('unit_id');

  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [bookingEntity, setBookingEntity] = useState(currentEntity !== 'all' ? currentEntity : 'sam_rumah');

  // Data states
  const [properties, setProperties] = useState([]);
  const [units, setUnits] = useState([]);
  const [autoFillData, setAutoFillData] = useState({});

  // Developer template states
  const [devTemplate, setDevTemplate] = useState(null);
  const [devTemplateFields, setDevTemplateFields] = useState(null);
  const [formData, setFormData] = useState({});

  // Payment state
  const [paymentInfo, setPaymentInfo] = useState({
    booking_fee_amount: '',
    payment_method: 'bank_transfer',
    bank_reference: '',
  });
  const [paymentProofFile, setPaymentProofFile] = useState(null);
  const [paymentProofPreview, setPaymentProofPreview] = useState(null);
  const [signatures, setSignatures] = useState({});

  // Preview overlay states
  const [showPreview, setShowPreview] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewPage, setPreviewPage] = useState(0);
  const [generatingPdf, setGeneratingPdf] = useState(false);

  // Form state
  const [selectedPropertyId, setSelectedPropertyId] = useState(preSelectedPropertyId || '');
  const [selectedUnitId, setSelectedUnitId] = useState(preSelectedUnitId || '');
  const [manualUnitNumber, setManualUnitNumber] = useState('');

  const selectedProperty = properties.find(p => p.id === selectedPropertyId);
  const isExclusive = selectedProperty?.is_exclusive !== false;
  const hasDevTemplate = !!devTemplateFields && devTemplateFields.total_fields > 0;

  // Steps: Property & Unit -> Developer Form -> Payment Details -> Confirmation
  const totalSteps = hasDevTemplate ? 4 : 3;
  const paymentStep = hasDevTemplate ? 3 : 2;

  useEffect(() => { loadProperties(); }, []);

  useEffect(() => {
    if (selectedPropertyId) loadPropertyData(selectedPropertyId);
  }, [selectedPropertyId]);

  useEffect(() => {
    if (selectedUnitId && autoFillData.property_id) loadUnitAutoFill();
  }, [selectedUnitId]);

  const loadProperties = async () => {
    try {
      const res = await propertyAPI.getProperties();
      setProperties(res.data || []);
    } catch (error) {
      toast.error('Failed to load properties');
    } finally {
      setLoading(false);
    }
  };

  const loadPropertyData = async (propertyId) => {
    setLoading(true);
    try {
      const unitsRes = await unitAPI.getUnits({ property_id: propertyId, status: 'available' });
      setUnits(unitsRes.data || []);

      // Load auto-fill data
      try {
        const templateRes = await propertyAPI.getBookingTemplate(propertyId);
        setAutoFillData(templateRes.data.auto_fill_data);
      } catch (e) {}

      // Check if property has a developer template
      const propertyRes = await propertyAPI.getProperty(propertyId);
      const property = propertyRes.data;
      // Auto-populate booking fee from property
      if (property?.total_booking_fee) {
        setPaymentInfo(prev => ({ ...prev, booking_fee_amount: property.total_booking_fee.toString() }));
      }
      if (property?.developer_template_id) {
        try {
          const dtRes = await developerTemplateAPI.getTemplate(property.developer_template_id);
          setDevTemplate(dtRes.data);
          
          if (dtRes.data.field_count > 0 || (dtRes.data.fields && dtRes.data.fields.length > 0)) {
            const fieldsRes = await developerTemplateAPI.getFieldsForBooking(property.developer_template_id);
            setDevTemplateFields(fieldsRes.data);
            
            const initialData = {};
            for (const section of (fieldsRes.data.sections || [])) {
              for (const field of (section.fields || [])) {
                initialData[field.id] = '';
              }
            }
            setFormData(initialData);
          } else {
            setDevTemplate(null);
            setDevTemplateFields(null);
          }
        } catch (e) {
          console.error('Error loading developer template:', e);
          setDevTemplate(null);
          setDevTemplateFields(null);
        }
      } else {
        setDevTemplate(null);
        setDevTemplateFields(null);
      }
    } catch (error) {
      console.error('Error loading property data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadUnitAutoFill = async () => {
    try {
      const templateRes = await propertyAPI.getBookingTemplate(selectedPropertyId, selectedUnitId);
      setAutoFillData(templateRes.data.auto_fill_data);
    } catch (error) {}
  };

  const selectedUnit = units.find(u => u.id === selectedUnitId);

  const handlePaymentProofChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size must be under 10MB');
      return;
    }
    setPaymentProofFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setPaymentProofPreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const canProceed = (stepNum) => {
    if (stepNum === 1) {
      if (!selectedPropertyId) return false;
      if (isExclusive) return !!selectedUnitId;
      return !!manualUnitNumber.trim();
    }
    if (stepNum === 2 && hasDevTemplate) {
      for (const section of (devTemplateFields?.sections || [])) {
        for (const field of (section.fields || [])) {
          if (field.required && !formData[field.id]?.toString().trim()) return false;
        }
      }
      return true;
    }
    if (stepNum === paymentStep) {
      if (!paymentInfo.booking_fee_amount || parseFloat(paymentInfo.booking_fee_amount) <= 0) return false;
      if (!paymentInfo.payment_method) return false;
      if (!paymentInfo.bank_reference.trim()) return false;
      if (!paymentProofFile && !paymentProofPreview) return false;
      return true;
    }
    return true;
  };

  const validateStep = (stepNum) => {
    if (stepNum === paymentStep && !paymentProofFile && !paymentProofPreview) {
      toast.error('Payment proof upload is mandatory');
      return false;
    }
    return canProceed(stepNum);
  };

  const handleNext = () => {
    if (!validateStep(step)) {
      if (!canProceed(step)) {
        toast.error('Please fill in all required fields');
      }
      return;
    }
    setStep(step + 1);
  };

  const handlePreviewOverlay = async (page = 0) => {
    if (!devTemplate?.id) {
      toast.error('Template not loaded. Please re-select the property.');
      return;
    }
    setPreviewLoading(true);
    setShowPreview(true);
    try {
      if (!devTemplate.positions_detected) {
        toast.info('Detecting field positions...');
        try {
          await developerTemplateAPI.detectPositions(devTemplate.id);
          const refreshed = await developerTemplateAPI.getTemplate(devTemplate.id);
          setDevTemplate(refreshed.data);
        } catch (detectErr) {
          console.warn('Position detection skipped (may require admin):', detectErr);
        }
      }
      const res = await developerTemplateAPI.previewOverlay(devTemplate.id, { form_values: formData, page });
      setPreviewImage(res.data.image);
      setPreviewPage(page);
    } catch (error) {
      console.error('Preview overlay error:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate preview');
      setShowPreview(false);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleDownloadFilledPdf = async (bookingId = null) => {
    if (!devTemplate?.id) {
      toast.error('Template not loaded. Please re-select the property.');
      return;
    }
    setGeneratingPdf(true);
    try {
      if (!devTemplate.positions_detected) {
        try {
          await developerTemplateAPI.detectPositions(devTemplate.id);
        } catch (detectErr) {
          console.warn('Position detection skipped:', detectErr);
        }
      }
      const res = await developerTemplateAPI.generateFilled(devTemplate.id, { form_values: formData, booking_id: bookingId, signatures });
      const pdfUrl = `${BACKEND_URL}/api${res.data.pdf_url}`;
      window.open(pdfUrl, '_blank');
      toast.success(`PDF generated with ${res.data.filled_count} fields filled`);
    } catch (error) {
      console.error('Generate PDF error:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate PDF');
    } finally {
      setGeneratingPdf(false);
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      // Build form_data from dynamic fields
      const fd = { ...formData };
      for (const section of (devTemplateFields?.sections || [])) {
        for (const field of (section.fields || [])) {
          const v = formData[field.id];
          if (!v) continue;
          if (field.data_mapping === 'form_data.buyer_name') fd.buyer_name = v;
          if (field.data_mapping === 'form_data.buyer_ic') fd.buyer_ic = v;
          if (field.data_mapping === 'form_data.buyer_phone') fd.buyer_phone = v;
          if (field.data_mapping === 'form_data.buyer_email') fd.buyer_email = v;
          if (field.data_mapping === 'form_data.buyer_address') fd.buyer_address = v;
        }
      }
      if (!fd.buyer_name) fd.buyer_name = 'Customer';
      if (!fd.buyer_ic) fd.buyer_ic = '-';

      const bookingData = {
        property_id: selectedPropertyId,
        unit_id: isExclusive ? selectedUnitId : null,
        manual_unit_number: !isExclusive ? manualUnitNumber : null,
        developer_template_id: devTemplate?.id || null,
        entity: bookingEntity,
        form_data: fd,
        signatures: signatures,
        // Payment fields
        booking_fee_amount: parseFloat(paymentInfo.booking_fee_amount) || 0,
        payment_method: paymentInfo.payment_method,
        bank_reference: paymentInfo.bank_reference,
        payment_proof: paymentProofPreview, // base64
      };

      await bookingFormAPI.createWithForm(bookingData);
      toast.success('Booking created successfully! Payment proof submitted for verification.');
      navigate('/bookings');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create booking');
    } finally {
      setSubmitting(false);
    }
  };

  const renderField = (field) => {
    const value = formData[field.id] || '';
    const onChange = (val) => setFormData(prev => ({ ...prev, [field.id]: val }));
    const inputClass = "bg-gray-50 border-gray-100 text-gray-900 mt-1";

    switch (field.field_type) {
      case 'date':
        return <Input type="date" value={value} onChange={(e) => onChange(e.target.value)} className={inputClass} data-testid={`field-${field.id}`} />;
      case 'number':
        return <Input type="number" value={value} onChange={(e) => onChange(e.target.value)} className={inputClass} data-testid={`field-${field.id}`} />;
      case 'email':
        return <Input type="email" value={value} onChange={(e) => onChange(e.target.value)} className={inputClass} placeholder="email@example.com" data-testid={`field-${field.id}`} />;
      case 'phone':
        return <Input type="tel" value={value} onChange={(e) => onChange(e.target.value)} className={inputClass} placeholder="+60 12-345 6789" data-testid={`field-${field.id}`} />;
      case 'address':
        return <Textarea value={value} onChange={(e) => onChange(e.target.value)} className={`${inputClass} min-h-[80px]`} placeholder="Enter full address" data-testid={`field-${field.id}`} />;
      case 'checkbox':
        return (
          <div className="flex items-center gap-2 mt-2">
            <Checkbox checked={value === 'true' || value === true} onCheckedChange={(c) => onChange(c ? 'true' : 'false')} data-testid={`field-${field.id}`} />
            <span className="text-sm text-gray-500">{field.description || 'Check if applicable'}</span>
          </div>
        );
      case 'signature':
        return (
          <SignaturePad
            label={field.label || 'Signature'}
            onSignature={(data) => {
              setSignatures(prev => ({ ...prev, [field.id]: data }));
              onChange(data ? 'signed' : '');
            }}
            existingSignature={signatures[field.id]}
          />
        );
      default:
        return <Input value={value} onChange={(e) => onChange(e.target.value)} className={inputClass} placeholder={field.description || `Enter ${field.label.toLowerCase()}`} data-testid={`field-${field.id}`} />;
    }
  };

  const getSteps = () => {
    if (hasDevTemplate) {
      return [
        { num: 1, label: 'Property & Unit', icon: Building2 },
        { num: 2, label: 'Developer Form', icon: FileText },
        { num: 3, label: 'Payment Details', icon: Receipt },
        { num: 4, label: 'Confirmation', icon: CheckCircle },
      ];
    }
    return [
      { num: 1, label: 'Property & Unit', icon: Building2 },
      { num: 2, label: 'Payment Details', icon: Receipt },
      { num: 3, label: 'Confirmation', icon: CheckCircle },
    ];
  };

  const isConfirmStep = step === totalSteps;

  if (loading && properties.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[#4E50FF]" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="create-booking-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" onClick={() => navigate('/bookings')} className="text-gray-500" data-testid="back-btn">
          <ArrowLeft className="w-4 h-4 mr-2" />Back
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Create New Booking</h1>
          <p className="text-gray-500">Complete the form to create a property booking</p>
        </div>
      </div>

      {/* Progress Steps */}
      <div className="flex items-center justify-between bg-gray-50 border border-gray-100 rounded-lg p-4">
        {getSteps().map((s, idx) => (
          <div key={s.num} className="flex items-center">
            <div className={`flex items-center gap-2 ${step >= s.num ? 'text-[#4E50FF]' : 'text-gray-400'}`}>
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                step > s.num ? 'bg-[#4E50FF] text-gray-900' :
                step === s.num ? 'bg-[#4E50FF]/10 border-2 border-[#4E50FF] text-[#4E50FF]' :
                'bg-gray-100 text-gray-400'
              }`}>
                {step > s.num ? <CheckCircle className="w-5 h-5" /> : <s.icon className="w-5 h-5" />}
              </div>
              <span className="hidden md:block text-sm font-medium">{s.label}</span>
            </div>
            {idx < getSteps().length - 1 && (
              <div className={`w-12 lg:w-24 h-0.5 mx-2 ${step > s.num ? 'bg-[#4E50FF]' : 'bg-gray-200'}`} />
            )}
          </div>
        ))}
      </div>

      {/* Dev Template Banner */}
      {hasDevTemplate && selectedPropertyId && (
        <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-4">
          <div className="flex items-center gap-2 text-purple-400">
            <Wand2 className="w-5 h-5" />
            <span className="font-medium">Developer Form: {devTemplate?.name}</span>
            <Badge className="bg-purple-100 text-purple-700 text-xs">{devTemplateFields?.total_fields} fields</Badge>
          </div>
          <p className="text-sm text-gray-500 mt-1">
            Fields from {devTemplate?.developer_name}'s booking form will be used ({devTemplate?.project_name})
          </p>
        </div>
      )}

      {/* No template warning */}
      {!hasDevTemplate && selectedPropertyId && !loading && (
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4">
          <div className="flex items-center gap-2 text-amber-400">
            <FileText className="w-5 h-5" />
            <span className="font-medium">No Developer Booking Form</span>
          </div>
          <p className="text-sm text-gray-500 mt-1">
            This property does not have a developer booking form template. Please upload one via Admin &gt; Developer PDFs before creating a booking.
          </p>
        </div>
      )}

      <Card className="border-gray-100">
        <CardContent className="pt-6">
          {/* Step 1: Property & Unit Selection */}
          {step === 1 && (
            <div className="space-y-6">
              {/* Entity Selection */}
              <div>
                <Label className="text-gray-700 text-base">Booking Entity *</Label>
                <p className="text-sm text-gray-400 mb-2">Select which entity this booking is under</p>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => setBookingEntity('sam_rumah')}
                    className={`flex items-center gap-3 p-3 rounded-lg border transition-all ${
                      bookingEntity === 'sam_rumah'
                        ? 'border-emerald-500 bg-emerald-500/10'
                        : 'border-gray-200 bg-gray-50 hover:border-gray-100'
                    }`}
                    data-testid="booking-entity-sr"
                  >
                    <div className={`w-9 h-9 rounded-md flex items-center justify-center font-bold text-sm ${
                      bookingEntity === 'sam_rumah' ? 'bg-emerald-500 text-gray-900' : 'bg-gray-100 text-gray-500'
                    }`}>SR</div>
                    <div className="text-left">
                      <p className={`text-sm font-semibold ${bookingEntity === 'sam_rumah' ? 'text-[#4E50FF]' : 'text-gray-700'}`}>SAM Rumah</p>
                      <p className="text-[10px] text-gray-400">Customer cashback</p>
                    </div>
                  </button>
                  <button
                    onClick={() => setBookingEntity('sp_sam')}
                    className={`flex items-center gap-3 p-3 rounded-lg border transition-all ${
                      bookingEntity === 'sp_sam'
                        ? 'border-blue-500 bg-blue-500/10'
                        : 'border-gray-200 bg-gray-50 hover:border-gray-100'
                    }`}
                    data-testid="booking-entity-sp"
                  >
                    <div className={`w-9 h-9 rounded-md flex items-center justify-center font-bold text-sm ${
                      bookingEntity === 'sp_sam' ? 'bg-blue-500 text-gray-900' : 'bg-gray-100 text-gray-500'
                    }`}>SP</div>
                    <div className="text-left">
                      <p className={`text-sm font-semibold ${bookingEntity === 'sp_sam' ? 'text-blue-400' : 'text-gray-700'}`}>SP SAM Property</p>
                      <p className="text-[10px] text-gray-400">Direct commission</p>
                    </div>
                  </button>
                </div>
              </div>

              <Separator className="bg-gray-100" />

              <div>
                <Label className="text-gray-700 text-base">Select Property *</Label>
                <p className="text-sm text-gray-400 mb-2">Choose the property for this booking</p>
                <Select value={selectedPropertyId} onValueChange={setSelectedPropertyId}>
                  <SelectTrigger className="bg-gray-50 border-gray-100 text-gray-900" data-testid="property-select">
                    <SelectValue placeholder="Select a property" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-gray-200">
                    {properties.map((property) => (
                      <SelectItem key={property.id} value={property.id}>
                        <div className="flex items-center gap-2">
                          <Building2 className="w-4 h-4 text-gray-500" />
                          <span>{property.name}</span>
                          <span className="text-gray-400">- {property.location}</span>
                          {property.developer_template_id && <Badge className="text-[10px] bg-purple-50 text-purple-600 ml-1">Dev Form</Badge>}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {selectedPropertyId && (
                <>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                      <Building2 className="w-4 h-4 text-[#4E50FF]" />
                      Property Details (Auto-filled)
                      <Badge className={`ml-auto text-xs ${isExclusive ? 'bg-[#4E50FF]/20 text-[#4E50FF] border border-[#4E50FF]/30' : 'bg-amber-500/20 text-amber-400 border border-amber-500/30'}`}>
                        {isExclusive ? 'Exclusive' : 'Non-Exclusive'}
                      </Badge>
                    </h4>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div><span className="text-gray-400">Name:</span> <span className="text-gray-900 ml-2">{autoFillData.property_name || selectedProperty?.name}</span></div>
                      <div><span className="text-gray-400">Location:</span> <span className="text-gray-900 ml-2">{autoFillData.property_location || selectedProperty?.location}</span></div>
                      <div><span className="text-gray-400">Developer:</span> <span className="text-gray-900 ml-2">{autoFillData.property_developer || selectedProperty?.developer}</span></div>
                      <div><span className="text-gray-400">Type:</span> <span className="text-gray-900 ml-2 capitalize">{autoFillData.property_type || selectedProperty?.property_type}</span></div>
                    </div>
                  </div>

                  <Separator className="bg-gray-100" />

                  {isExclusive ? (
                    <div>
                      <Label className="text-gray-700 text-base">Select Unit *</Label>
                      <p className="text-sm text-gray-400 mb-2">{units.length > 0 ? `${units.length} available units` : 'Loading units...'}</p>
                      <Select value={selectedUnitId} onValueChange={setSelectedUnitId}>
                        <SelectTrigger className="bg-gray-50 border-gray-100 text-gray-900" data-testid="unit-select">
                          <SelectValue placeholder="Select a unit" />
                        </SelectTrigger>
                        <SelectContent className="bg-white border-gray-200 max-h-60">
                          {units.map((unit) => (
                            <SelectItem key={unit.id} value={unit.id}>
                              <div className="flex items-center gap-2">
                                <Home className="w-4 h-4 text-gray-500" />
                                <span>{unit.unit_number}</span>
                                {unit.unit_type && <Badge variant="outline" className="text-xs">{unit.unit_type}</Badge>}
                                <span className="text-[#4E50FF] font-mono ml-4">{formatCurrency(unit.price)}</span>
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  ) : (
                    <div>
                      <Label className="text-gray-700 text-base">Unit Number *</Label>
                      <p className="text-sm text-amber-400/70 mb-2">Non-exclusive project - enter unit number manually</p>
                      <Input
                        value={manualUnitNumber}
                        onChange={(e) => setManualUnitNumber(e.target.value)}
                        placeholder="Enter unit number (e.g., A-12-05)"
                        className="bg-gray-50 border-gray-100 text-gray-900"
                        data-testid="manual-unit-input"
                      />
                    </div>
                  )}

                  {isExclusive && selectedUnit && (
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h4 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                        <Home className="w-4 h-4 text-[#4E50FF]" />
                        Unit Details (Auto-filled)
                      </h4>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div><span className="text-gray-400">Unit No:</span> <span className="text-gray-900 ml-2">{selectedUnit.unit_number}</span></div>
                        <div><span className="text-gray-400">Floor:</span> <span className="text-gray-900 ml-2">{selectedUnit.floor || '-'}</span></div>
                        <div><span className="text-gray-400">Size:</span> <span className="text-gray-900 ml-2">{selectedUnit.size_sqft || '-'} sqft</span></div>
                        <div><span className="text-gray-400">Price:</span> <span className="text-[#4E50FF] ml-2 font-mono">{formatCurrency(selectedUnit.price)}</span></div>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* Step 2: Developer Form Fields */}
          {step === 2 && hasDevTemplate && (
            <div className="space-y-6" data-testid="developer-form-fields">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Wand2 className="w-5 h-5 text-purple-400" />
                  <h3 className="text-lg font-medium text-gray-900">{devTemplate?.name}</h3>
                  <Badge className="bg-purple-100 text-purple-700 text-xs">{devTemplate?.developer_name}</Badge>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePreviewOverlay(0)}
                  className="border-purple-500/50 text-purple-400 hover:bg-purple-500/10"
                  data-testid="preview-on-pdf-btn"
                >
                  <Eye className="w-4 h-4 mr-1" />
                  Preview on PDF
                </Button>
              </div>

              {(devTemplateFields?.sections || []).map((section) => (
                <div key={section.name}>
                  <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3 pb-2 border-b border-gray-200">
                    {section.name}
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {section.fields.map((field) => (
                      <div key={field.id} className={field.field_type === 'address' ? 'md:col-span-2' : ''}>
                        <Label className="text-gray-700 text-sm flex items-center gap-1">
                          {field.label}
                          {field.required && <span className="text-red-400">*</span>}
                          <Badge variant="outline" className="text-[10px] px-1 py-0 ml-1">{field.field_type}</Badge>
                        </Label>
                        {field.description && (
                          <p className="text-[11px] text-gray-400 mt-0.5">{field.description}</p>
                        )}
                        {renderField(field)}
                      </div>
                    ))}
                  </div>
                </div>
              ))}

              {/* Signature Section */}
              <Separator className="bg-gray-200" />
              <div>
                <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3 pb-2 border-b border-gray-200">
                  Signatures
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <SignaturePad
                    label="Buyer / Purchaser Signature *"
                    onSignature={(data) => setSignatures(prev => ({ ...prev, buyer: data }))}
                    existingSignature={signatures.buyer}
                  />
                  <SignaturePad
                    label="Witness / Agent Signature"
                    onSignature={(data) => setSignatures(prev => ({ ...prev, agent: data }))}
                    existingSignature={signatures.agent}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Payment Details */}
          {step === paymentStep && (
            <div className="space-y-6" data-testid="payment-details-step">
              <div className="flex items-center gap-2 mb-2">
                <Receipt className="w-5 h-5 text-[#4E50FF]" />
                <h3 className="text-lg font-medium text-gray-900">Booking Fee Payment</h3>
              </div>
              <p className="text-sm text-gray-500 -mt-4">Upload your booking fee payment proof. All fields are mandatory.</p>

              {/* Booking Fee Summary */}
              {selectedProperty?.total_booking_fee > 0 && (
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-4 border border-blue-100" data-testid="booking-fee-summary">
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Total Required</p>
                      <p className="text-lg font-bold text-gray-900">RM {Number(selectedProperty.total_booking_fee).toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 mb-1">This Payment</p>
                      <p className="text-lg font-bold text-emerald-600">RM {Number(paymentInfo.booking_fee_amount || 0).toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Outstanding</p>
                      <p className={`text-lg font-bold ${(selectedProperty.total_booking_fee - (parseFloat(paymentInfo.booking_fee_amount) || 0)) <= 0 ? 'text-emerald-600' : 'text-amber-600'}`}>
                        RM {Math.max(0, selectedProperty.total_booking_fee - (parseFloat(paymentInfo.booking_fee_amount) || 0)).toLocaleString()}
                      </p>
                    </div>
                  </div>
                  {(selectedProperty.total_booking_fee - (parseFloat(paymentInfo.booking_fee_amount) || 0)) > 0 && (
                    <p className="text-xs text-amber-600 text-center mt-2">Partial payment allowed. Remaining balance can be paid later before SPA signing.</p>
                  )}
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-700">Booking Fee Amount (RM) *</Label>
                  <Input
                    type="number"
                    value={paymentInfo.booking_fee_amount}
                    onChange={(e) => setPaymentInfo({ ...paymentInfo, booking_fee_amount: e.target.value })}
                    className="bg-gray-50 border-gray-100 text-gray-900 mt-1"
                    placeholder="e.g., 10000"
                    data-testid="booking-fee-amount"
                  />
                </div>
                <div>
                  <Label className="text-gray-700">Payment Method *</Label>
                  <Select value={paymentInfo.payment_method} onValueChange={(v) => setPaymentInfo({ ...paymentInfo, payment_method: v })}>
                    <SelectTrigger className="bg-gray-50 border-gray-100 text-gray-900 mt-1" data-testid="payment-method-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-gray-200">
                      <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                      <SelectItem value="cheque">Cheque</SelectItem>
                      <SelectItem value="cash">Cash</SelectItem>
                      <SelectItem value="credit_card">Credit Card</SelectItem>
                      <SelectItem value="online_banking">Online Banking</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label className="text-gray-700">Bank Reference Number *</Label>
                <Input
                  value={paymentInfo.bank_reference}
                  onChange={(e) => setPaymentInfo({ ...paymentInfo, bank_reference: e.target.value })}
                  className="bg-gray-50 border-gray-100 text-gray-900 mt-1"
                  placeholder="e.g., TRX-2026030712345"
                  data-testid="bank-reference"
                />
              </div>

              <Separator className="bg-gray-100" />

              {/* Payment Proof Upload */}
              <div>
                <Label className="text-gray-700 text-base">Payment Proof Upload *</Label>
                <p className="text-sm text-gray-400 mb-3">Upload a screenshot or photo of your payment receipt/slip (max 10MB)</p>
                
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handlePaymentProofChange}
                  accept="image/*,.pdf"
                  className="hidden"
                  data-testid="payment-proof-input"
                />

                {!paymentProofPreview ? (
                  <div
                    onClick={() => fileInputRef.current?.click()}
                    className="border-2 border-dashed border-gray-200 rounded-lg p-8 text-center cursor-pointer hover:border-[#4E50FF]/50 hover:bg-[#4E50FF]/5 transition-all"
                    data-testid="payment-proof-dropzone"
                  >
                    <Upload className="w-10 h-10 text-gray-400 mx-auto mb-3" />
                    <p className="text-gray-500 font-medium">Click to upload payment proof</p>
                    <p className="text-gray-400 text-sm mt-1">Supports: JPG, PNG, PDF (max 10MB)</p>
                  </div>
                ) : (
                  <div className="relative">
                    <div className="border border-gray-200 rounded-lg overflow-hidden bg-gray-50 p-3">
                      <div className="flex items-center gap-3">
                        <div className="w-20 h-20 rounded-lg overflow-hidden bg-gray-100 flex items-center justify-center flex-shrink-0">
                          {paymentProofPreview.startsWith('data:image') ? (
                            <img src={paymentProofPreview} alt="Payment proof" className="w-full h-full object-cover" />
                          ) : (
                            <FileText className="w-8 h-8 text-gray-500" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-gray-900 text-sm font-medium truncate">{paymentProofFile?.name}</p>
                          <p className="text-gray-400 text-xs mt-0.5">{(paymentProofFile?.size / 1024).toFixed(1)} KB</p>
                          <Badge className="bg-emerald-500/20 text-[#4E50FF] border-emerald-500/30 text-xs mt-1">Uploaded</Badge>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => { setPaymentProofFile(null); setPaymentProofPreview(null); }}
                          className="text-gray-500 hover:text-red-400"
                          data-testid="remove-payment-proof"
                        >
                          Remove
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Step 4: Confirmation */}
          {isConfirmStep && (
            <div className="space-y-6">
              <div className="text-center mb-6">
                <CheckCircle className="w-16 h-16 text-[#4E50FF] mx-auto mb-4" />
                <h2 className="text-xl font-bold text-gray-900">Review Your Booking</h2>
                <p className="text-gray-500">Please confirm all details before submitting</p>
              </div>

              {/* Property & Unit */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-medium text-[#4E50FF] mb-3 flex items-center gap-2">
                  <Building2 className="w-4 h-4" />Property & Unit
                </h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div><span className="text-gray-400">Property:</span> <span className="text-gray-900">{selectedProperty?.name}</span></div>
                  <div><span className="text-gray-400">Location:</span> <span className="text-gray-900">{selectedProperty?.location}</span></div>
                  <div><span className="text-gray-400">Unit:</span> <span className="text-gray-900">{isExclusive ? selectedUnit?.unit_number : manualUnitNumber}</span></div>
                  {isExclusive && selectedUnit?.price && (
                    <div><span className="text-gray-400">Price:</span> <span className="text-[#4E50FF] font-mono">{formatCurrency(selectedUnit?.price)}</span></div>
                  )}
                </div>
              </div>

              {/* Developer Form Data Summary */}
              {hasDevTemplate && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-medium text-purple-400 mb-3 flex items-center gap-2">
                    <Wand2 className="w-4 h-4" />{devTemplate?.name} - Form Data
                  </h4>
                  {(devTemplateFields?.sections || []).map((section) => (
                    <div key={section.name} className="mb-3">
                      <h5 className="text-xs font-semibold text-gray-400 uppercase mb-1">{section.name}</h5>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        {section.fields.filter(f => formData[f.id]).map((field) => (
                          <div key={field.id}>
                            <span className="text-gray-400">{field.label}:</span>
                            <span className="text-gray-900 ml-2">{formData[field.id]}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Payment Summary */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-medium text-[#4E50FF] mb-3 flex items-center gap-2">
                  <CreditCard className="w-4 h-4" />Payment Details
                </h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div><span className="text-gray-400">Booking Fee:</span> <span className="text-[#4E50FF] font-mono ml-2">RM {parseFloat(paymentInfo.booking_fee_amount || 0).toLocaleString()}</span></div>
                  <div><span className="text-gray-400">Payment Method:</span> <span className="text-gray-900 ml-2 capitalize">{paymentInfo.payment_method.replace('_', ' ')}</span></div>
                  <div><span className="text-gray-400">Bank Reference:</span> <span className="text-gray-900 ml-2">{paymentInfo.bank_reference}</span></div>
                  <div className="flex items-center gap-2">
                    <span className="text-gray-400">Payment Proof:</span>
                    <Badge className="bg-emerald-500/20 text-[#4E50FF] border-emerald-500/30 text-xs">Uploaded</Badge>
                  </div>
                </div>
              </div>

              {devTemplate && (
                <div className="flex items-center justify-center gap-3 pt-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePreviewOverlay(0)}
                    className="border-purple-500/50 text-purple-400 hover:bg-purple-500/10"
                    data-testid="preview-filled-pdf-btn"
                  >
                    <Eye className="w-4 h-4 mr-1" />Preview Filled PDF
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDownloadFilledPdf()}
                    disabled={generatingPdf}
                    className="border-emerald-500/50 text-[#4E50FF] hover:bg-emerald-500/10"
                    data-testid="download-filled-pdf-btn"
                  >
                    {generatingPdf ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Download className="w-4 h-4 mr-1" />}
                    Download Filled PDF
                  </Button>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Preview Overlay Dialog */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-auto border-gray-200">
          <DialogHeader>
            <DialogTitle className="text-gray-900 flex items-center gap-2">
              <Eye className="w-5 h-5 text-purple-400" />
              PDF Preview with Data Overlay
              <Badge className="bg-purple-100 text-purple-700 text-xs ml-2">{devTemplate?.name}</Badge>
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex items-center justify-center gap-3">
              <Button variant="ghost" size="sm" onClick={() => handlePreviewOverlay(previewPage - 1)} disabled={previewPage === 0 || previewLoading} className="text-gray-700">
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="text-sm text-gray-500">Page {previewPage + 1} of {devTemplate?.page_count || 1}</span>
              <Button variant="ghost" size="sm" onClick={() => handlePreviewOverlay(previewPage + 1)} disabled={previewPage >= (devTemplate?.page_count || 1) - 1 || previewLoading} className="text-gray-700">
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
            {previewLoading ? (
              <div className="flex items-center justify-center h-[600px] bg-gray-50 rounded-lg">
                <div className="text-center">
                  <Loader2 className="w-8 h-8 animate-spin text-purple-400 mx-auto mb-2" />
                  <p className="text-gray-500 text-sm">Generating preview overlay...</p>
                </div>
              </div>
            ) : previewImage ? (
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <img src={`data:image/jpeg;base64,${previewImage}`} alt="Filled PDF Preview" className="w-full" data-testid="preview-overlay-image" />
              </div>
            ) : (
              <div className="flex items-center justify-center h-[400px] bg-gray-50 rounded-lg text-gray-400">No preview available</div>
            )}
            <div className="flex items-center justify-center gap-4 text-xs text-gray-400">
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded bg-blue-200 border border-blue-400"></div>
                <span>Filled field data (highlighted in blue)</span>
              </div>
            </div>
            <div className="flex justify-end">
              <Button onClick={() => handleDownloadFilledPdf()} disabled={generatingPdf} className="bg-[#4E50FF] hover:bg-[#3436CC]" data-testid="dialog-download-pdf-btn">
                {generatingPdf ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Download className="w-4 h-4 mr-1" />}
                Download Filled PDF
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Navigation */}
      <div className="flex justify-between">
        {step > 1 ? (
          <Button variant="outline" onClick={() => setStep(step - 1)} className="border-gray-200 text-gray-700" data-testid="prev-step">
            <ArrowLeft className="w-4 h-4 mr-2" />Back
          </Button>
        ) : <div />}

        {!isConfirmStep ? (
          <Button onClick={handleNext} className="bg-[#4E50FF] hover:bg-[#3436CC]" disabled={!canProceed(step)} data-testid="next-step">
            Next<ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        ) : (
          <Button onClick={handleSubmit} className="bg-[#4E50FF] hover:bg-[#3436CC]" disabled={submitting} data-testid="submit-booking">
            {submitting ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Creating...</>) : (<><CheckCircle className="w-4 h-4 mr-2" />Create Booking</>)}
          </Button>
        )}
      </div>
    </div>
  );
};

export default CreateBooking;
